import React, { useMemo, useState } from 'react';
import {
    createColumnHelper,
    useReactTable,
    getCoreRowModel,
    getSortedRowModel,
    getPaginationRowModel,
    flexRender,
} from '@tanstack/react-table';
import LinkWithTooltip from '../Common/LinkWithTooltip.jsx';

const columnHelper = createColumnHelper();

const naturalSort = (rowA, rowB, columnId) => {
    const a = rowA.getValue(columnId);
    const b = rowB.getValue(columnId);

    const isNumericA = !isNaN(a);
    const isNumericB = !isNaN(b);

    if (isNumericA && isNumericB) {
        return Number(a) - Number(b);
    }
    if (isNumericA) return -1;
    if (isNumericB) return 1;

    return String(a).localeCompare(String(b), undefined, { numeric: true, sensitivity: 'base' });
};

export default function ReactTableWrapperUsers({
                                                   users,
                                                   t,
                                                   handleUnlock,
                                                   handleDelete,
                                               }) {
    const data = useMemo(() => {
        return users.map(user => ({
            id: user.userId,
            name: `${user.firstName} ${user.lastName}`,
            email: user.email,
            role: user.role,
            lock: user.lock,
            needSupport: user.needSupport,
            userName: user.userName,
        }));
    }, [users]);

    const columns = useMemo(() => [
        columnHelper.accessor('name', {
            header: t('user:name'),
            sortingFn: naturalSort,
            size: 300,
        }),
        columnHelper.accessor('email', {
            header: t('user:email'),
            sortingFn: naturalSort,
            size: 300,
        }),
        columnHelper.accessor('role', {
            header: t('user:analyticsRole'),
            sortingFn: naturalSort,
            size: 200,
        }),
        columnHelper.accessor('lock', {
            header: t('user:accountStatus'),
            enableSorting: false,
            size: 200,
            cell: info => {
                const { lock, id, userName } = info.row.original;
                return (
                    <div className="unlock-left">
                        <span>{lock ? t('user:locked') : t('user:active')}</span>
                        {lock && (
                            <div className="action-icon-wrapper">
                                <LinkWithTooltip
                                    tooltip={t('user:unlockAccount')}
                                    onClick={handleUnlock}
                                    href="Settings"
                                    id={`${id}-${userName}`}
                                    tooltipIconClass="fa fa-lock"
                                    iconStyle={{ fontSize: '16px' }}
                                />
                            </div>
                        )}
                    </div>
                );
            },
        }),
        columnHelper.accessor('needSupport', {
            header: t('user:proxyUserMode'),
            enableSorting: false,
            size: 200,
            cell: info => (
                <span>
                    {info.getValue() ? t('user:enabled') : t('user:disabled')}
                </span>
            ),
        }),
        columnHelper.display({
            id: 'actions',
            header: t('user:actions'),
            enableSorting: false,
            size: 200,
            cell: info => {
                const { id, role, userName } = info.row.original;
                return (
                    <div className="actions-right">
                        <div className="action-icon-wrapper">
                            <LinkWithTooltip
                                tooltip={t('common:edit')}
                                href="editUser"
                                id={id}
                                tooltipIconClass="fa fa-edit"
                                iconStyle={{ fontSize: '16px' }}
                                linkName="editUser"
                            />
                        </div>
                        {role !== 'Admin' && (
                            <div className="action-icon-wrapper">
                                <LinkWithTooltip
                                    tooltip={t('common:delete')}
                                    onClick={handleDelete}
                                    href="Settings"
                                    id={`${id}-${userName}`}
                                    tooltipIconClass="fa fa-times"
                                    iconStyle={{ fontSize: '16px' }}
                                />
                            </div>
                        )}
                    </div>
                );
            },
        }),
    ], [t, handleUnlock, handleDelete]);

    const [sorting, setSorting] = useState([{ id: 'name', desc: false }]);

    const table = useReactTable({
        data,
        columns,
        state: { sorting },
        onSortingChange: setSorting,
        enableMultiSort: true,
        initialState: { pagination: { pageSize: 10 } },
        getCoreRowModel: getCoreRowModel(),
        getSortedRowModel: getSortedRowModel(),
        getPaginationRowModel: getPaginationRowModel(),
    });

    return (
        <>
            <div className="table-responsive">
                <table className="table table-hover -highlight">
                    <thead>
                    {table.getHeaderGroups().map(headerGroup => (
                        <tr key={headerGroup.id}>
                            {headerGroup.headers.map(header => (
                                <th
                                    key={header.id}
                                    onClick={() => {
                                        if (!header.column.getCanSort()) return;
                                        const isSorted = header.column.getIsSorted();
                                        const id = header.column.id;
                                        setSorting([{ id, desc: isSorted === 'asc' }]);
                                    }}
                                    style={{
                                        cursor: header.column.getCanSort() ? 'pointer' : 'default',
                                        whiteSpace: 'nowrap'
                                    }}
                                >
                                    {flexRender(header.column.columnDef.header, header.getContext())}
                                    {header.column.getCanSort() && (
                                        <span className={`sort-icon-stack ${header.column.getIsSorted() ? 'sorted' : ''}`}>
                                            {header.column.getIsSorted() === 'asc' && <i className="fa fa-sort-up" />}
                                            {header.column.getIsSorted() === 'desc' && <i className="fa fa-sort-down" />}
                                            {header.column.getIsSorted() === false && (
                                                <>
                                                    <i className="fa fa-sort-up" />
                                                    <i className="fa fa-sort-down" />
                                                </>
                                            )}
                                        </span>
                                    )}
                                </th>
                            ))}
                        </tr>
                    ))}
                    </thead>
                    <tbody>
                    {table.getRowModel().rows.length === 0 ? (
                        <tr>
                            <td colSpan={columns.length}>{t('user:noUsersFound')}</td>
                        </tr>
                    ) : (
                        table.getRowModel().rows.map(row => (
                            <tr key={row.id}>
                                {row.getVisibleCells().map(cell => (
                                    <td key={cell.id} style={{ width: `${cell.column.getSize()}px` }}>
                                        {flexRender(cell.column.columnDef.cell, cell.getContext())}
                                    </td>
                                ))}
                            </tr>
                        ))
                    )}
                    </tbody>
                </table>
            </div>

            {/* Pagination */}
            <div className="pagination-controls">
                <button className="page-button pb-s" onClick={() => table.firstPage()} disabled={!table.getCanPreviousPage()}>
                    {'<<'}
                </button>
                <button className="page-button pb-l" onClick={() => table.previousPage()} disabled={!table.getCanPreviousPage()}>
                    {t('common:previous')}
                </button>
                <span className="flex items-center gap-1 page-span">
                    {t('common:page')}{' '}
                    <input
                        type="number"
                        min={1}
                        max={table.getPageCount()}
                        value={table.getState().pagination.pageIndex + 1}
                        onChange={e => {
                            const page = e.target.value ? Number(e.target.value) - 1 : 0;
                            table.setPageIndex(page);
                        }}
                        className="page-input"
                    />{' '}
                    {t('common:of')} {table.getPageCount()}
                </span>
                <button className="page-button pb-l" onClick={() => table.nextPage()} disabled={!table.getCanNextPage()}>
                    {t('common:next')}
                </button>
                <button className="page-button pb-s" onClick={() => table.lastPage()} disabled={!table.getCanNextPage()}>
                    {'>>'}
                </button>
                {/*<select
                    className="page-select"
                    style={{ fontSize: '12px' }}
                    value={table.getState().pagination.pageSize}
                    onChange={e => table.setPageSize(Number(e.target.value))}
                >
                    {[10, 25, 50].map(size => (
                        <option key={size} value={size}>
                            {size} Rows
                        </option>
                    ))}
                </select>*/}
            </div>
        </>
    );
}